import { useLanguage } from "@/contexts/LanguageContext";
import pyramidImage from "@/assets/pyramid.png";
import arcImage from "@/assets/arc.png";
import torusImage from "@/assets/torus.png";
import spiralImage from "@/assets/spiral.png";
import cubeImage from "@/assets/cube.png";
import sphereImage from "@/assets/sphere.png";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const ExpertiseSection = () => {
  const { t } = useLanguage();
  
  const capabilities = [
    {
      image: pyramidImage,
      titleKey: "expertise.visual",
      descKey: "expertise.visualDesc",
    },
    {
      image: arcImage,
      titleKey: "expertise.performance",
      descKey: "expertise.performanceDesc",
    },
    {
      image: torusImage,
      titleKey: "expertise.retention",
      descKey: "expertise.retentionDesc",
    },
    {
      image: spiralImage,
      titleKey: "expertise.social",
      descKey: "expertise.socialDesc",
    },
    {
      image: cubeImage,
      titleKey: "expertise.website",
      descKey: "expertise.websiteDesc",
    },
    {
      image: sphereImage,
      titleKey: "expertise.ai",
      descKey: "expertise.aiDesc",
    },
  ];

  return (
    <section id="expertise" className="pt-20 md:pt-48 pb-16 md:pb-24 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8 text-center text-glow-hover uppercase">
          {t("expertise.title")} <span className="text-holographic">{t("expertise.capabilities")}</span>
        </h2>
        <p className="text-lg md:text-xl text-center text-muted-foreground mb-8 md:mb-12 px-4">
          {t("expertise.subtitle")}
        </p>

        <div className="relative max-w-5xl mx-auto overflow-visible">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full overflow-visible"
          >
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background via-background/50 to-transparent z-10 pointer-events-none" />
            
            <CarouselContent className="-ml-2 md:-ml-4">
              {capabilities.map((capability, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-[95%] sm:basis-[90%] md:basis-[85%] overflow-visible">
                  <div className="relative group">
                    {/* Holographic shadow effect */}
                    <div className="absolute -inset-2 bg-gradient-to-r from-[hsl(var(--holographic-cyan))]/30 via-[hsl(var(--holographic-purple))]/25 to-[hsl(var(--holographic-pink))]/30 rounded-2xl blur-2xl opacity-50 group-hover:opacity-80 transition-opacity duration-500" />
                    <div className="absolute -inset-1 bg-gradient-to-br from-[hsl(var(--holographic-cyan))]/15 to-[hsl(var(--holographic-purple))]/15 rounded-2xl blur-md" />
                    
                    <div className="relative glass-card rounded-2xl p-6 md:p-8 border-2 border-[hsl(var(--holographic-cyan))]/30 hover:border-[hsl(var(--holographic-cyan))] transition-all duration-300 shadow-[0_0_40px_rgba(0,212,255,0.15),0_0_80px_rgba(157,78,221,0.1)]">
                      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                        <div className="flex-shrink-0">
                          <img
                            src={capability.image}
                            alt={t(capability.titleKey)}
                            className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 object-contain animate-float hover-lift relative z-10"
                          />
                        </div>

                        <div className="flex-1 text-center md:text-left space-y-3 md:space-y-4">
                          <h3 className="text-xl md:text-2xl lg:text-3xl font-bold hover-glow-text uppercase tracking-wide">
                            {t(capability.titleKey)}
                          </h3>
                          <p className="text-sm md:text-base lg:text-lg text-muted-foreground leading-relaxed">
                            {t(capability.descKey)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            <div className="hidden sm:block">
              <CarouselPrevious className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 bg-[hsl(var(--holographic-cyan))]/10 border-[hsl(var(--holographic-cyan))]/30 hover:bg-[hsl(var(--holographic-cyan))]/20 hover:border-[hsl(var(--holographic-cyan))] transition-all" />
              <CarouselNext className="absolute -right-8 md:-right-12 top-1/2 -translate-y-1/2 bg-[hsl(var(--holographic-cyan))]/10 border-[hsl(var(--holographic-cyan))]/30 hover:bg-[hsl(var(--holographic-cyan))]/20 hover:border-[hsl(var(--holographic-cyan))] transition-all" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;
